import { useState, useEffect } from 'react'
import i18n from '../../../i18n'
import { bruktbilfinansieringAPI } from '../../../services/api'
import type { DatakvalitetResponse, FreshnessResponse } from '../../../types/bruktbilfinansiering'
import type { BruktbilfinansieringFilters } from './useBruktbilfinansiering'

export function useDatakvalitet(bankOrgNr: string, filters: BruktbilfinansieringFilters) {
  const [data, setData] = useState<DatakvalitetResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const refetch = async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await bruktbilfinansieringAPI.getDatakvalitet(bankOrgNr, {
        start_date: filters.startDate,
        end_date: filters.endDate,
        dealer_postnummer: filters.dealerPostnummer ?? undefined,
        buyer_postnummer: filters.buyerPostnummer ?? undefined,
        classes: filters.classes?.length ? filters.classes : undefined,
      })
      setData(res.data)
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : i18n.t('errors.unknown', { ns: 'bbf' }))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    refetch()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bankOrgNr, filters.startDate, filters.endDate, filters.dealerPostnummer, filters.buyerPostnummer, JSON.stringify(filters.classes)])

  return { data, loading, error, refetch }
}

/**
 * Lightweight hook for the cross-tab freshness pill.
 *
 * Calls /freshness, which returns the freshness block and nothing else
 * (bilpriserproff#281). It used to call /datakvalitet with today=today,
 * which averaged 73 s computing five further blocks this hook discarded —
 * on every page load of the module. /freshness takes no date parameters:
 * every field is a global maximum.
 */
const FRESHNESS_TTL_MS = 5 * 60 * 1000
const freshnessCache = new Map<string, { ts: number; data: FreshnessResponse['freshness'] }>()

export function useDatakvalitetFreshness(bankOrgNr: string) {
  const [data, setData] = useState<FreshnessResponse['freshness'] | null>(() => {
    const cached = freshnessCache.get(bankOrgNr)
    return cached && Date.now() - cached.ts < FRESHNESS_TTL_MS ? cached.data : null
  })

  useEffect(() => {
    const cached = freshnessCache.get(bankOrgNr)
    if (cached && Date.now() - cached.ts < FRESHNESS_TTL_MS) {
      setData(cached.data)
      return
    }
    bruktbilfinansieringAPI
      .getFreshness(bankOrgNr)
      .then(res => {
        const fresh = (res.data as FreshnessResponse).freshness
        freshnessCache.set(bankOrgNr, { ts: Date.now(), data: fresh })
        setData(fresh)
      })
      // The pill still degrades to not rendering, but say why. Swallowing
      // this is how a 73-second call on every page load stayed invisible.
      .catch(err => console.error('[bruktbilfinansiering] freshness failed', err))
  }, [bankOrgNr])

  return data
}
